import EditIcon from "@mui/icons-material/Edit";
import ErrorIcon from "@mui/icons-material/Error";
import SaveIcon from "@mui/icons-material/Save";
import { Typography, IconButton, Input } from "@mui/material";
import { Formik } from "formik";
import { ReactNode, useState } from "react";
import * as Yup from "yup";

import {
  GetTenDlcCampaignsForSubaccountQuery,
  UpdateNumberLimitMutation,
  UpdateNumberLimitMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  LIST_10DLC_CAMPAIGNS,
  UPDATE_NUMBER_LIMIT,
} from "~frontend/graphql/tenDlcCampaigns";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

export const NumberLimit = ({
  campaign,
}: {
  campaign: GetTenDlcCampaignsForSubaccountQuery["tenDlcCampaignsForSubaccount"][number];
}): ReactNode => {
  const [isEditing, setIsEditing] = useState(false);
  const { subaccount } = useSubaccount()!;

  const [updateNumberLimit] = useMutation<
    UpdateNumberLimitMutation,
    UpdateNumberLimitMutationVariables
  >(UPDATE_NUMBER_LIMIT, {
    onErrorSnackbarMessage: "Error updating number limit",
    refetchQueries: [LIST_10DLC_CAMPAIGNS],
  });

  return (
    <FlexBox gap={1} alignItems="center">
      <Typography variant="body3">
        <strong>Number limit:</strong>{" "}
        {isEditing ? (
          <Formik
            validationSchema={Yup.object().shape({
              numberLimit: Yup.number()
                .required()
                .min(
                  campaign.currentNumberCount || 1,
                  `Number limit cannot be set to less than the current number count of ${campaign.currentNumberCount || 1}`
                )
                .max(5000, "Number limit cannot be set to more than 5000"),
            })}
            initialValues={{ numberLimit: campaign.numberLimit }}
            onSubmit={async ({ numberLimit }) => {
              await updateNumberLimit({
                variables: {
                  input: {
                    tcrCampaignId: campaign.tcrCampaignId,
                    numberLimit,
                    subaccountId: subaccount.id,
                  },
                },
              });
              setIsEditing(false);
            }}
          >
            {({
              handleSubmit,
              errors,
              isSubmitting,
              handleChange,
              isValid,
            }) => (
              <>
                <Input
                  type="number"
                  id="numberLimit"
                  name="numberLimit"
                  disabled={isSubmitting}
                  defaultValue={campaign.numberLimit}
                  onChange={handleChange}
                  inputProps={{ min: campaign.currentNumberCount, max: 5000 }}
                />
                <IconButton
                  size="small"
                  disabled={isSubmitting || !isValid}
                  onClick={() => handleSubmit()}
                >
                  <SaveIcon sx={{ fontSize: 15 }} />
                </IconButton>
                {errors.numberLimit && (
                  <FlexBox alignItems="center" gap={1}>
                    <ErrorIcon sx={{ fontSize: 15, color: palette.red }} />{" "}
                    {errors.numberLimit}
                  </FlexBox>
                )}
              </>
            )}
          </Formik>
        ) : (
          <>
            <span>{campaign.numberLimit}</span>
            <IconButton size="small" onClick={() => setIsEditing(true)}>
              <EditIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </>
        )}
      </Typography>
    </FlexBox>
  );
};
